import React, { useState, useEffect } from 'react';
import { Clock, Phone, MapPin, Instagram } from 'lucide-react';
import { _StoreInfo, WorkingHours } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface RestaurantInfoProps {
  storeInfo: _StoreInfo;
}

const RestaurantInfo: React.FC<RestaurantInfoProps> = ({ storeInfo }) => {
  const { t, region } = useLanguage();
  const [bannerImages, setBannerImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  useEffect(() => {
    const fetchBannerImages = async () => {
      try {
        const storeId = storeInfo?.id || '';
        
        const response = await fetch(`https://gandalf.orwi.app/api/image/storeBanners`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Apikey': 'YWxpLTEyMzQ1MTEyNDU2NTQzMg',
            'Region': region
          },
          body: JSON.stringify({ id: storeId })
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch banner images');
        }
        
        const data = await response.json();

        if (data && Array.isArray(data.response)) {
          const bannerUrls = data.response.map((banner: any) => banner.base64);
          setBannerImages(bannerUrls);
        } else {
          console.error('Invalid banner data format:', data);
        }
      } catch (error) {
        console.error('Error fetching banner images:', error);
      }
    };
    
    fetchBannerImages();
  }, [storeInfo?.id, region]);
  
  useEffect(() => {
    if (bannerImages.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === bannerImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);
    
    return () => clearInterval(interval);
  }, [bannerImages]);
  
  const formatWorkingHours = (workingHours: WorkingHours[] | undefined): string => {
    if (!workingHours) return t('workingHours.unknown');
    
    // const days = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
    const today = new Date().getDay();
    
    const todayHours = workingHours.find(wh => wh.day === today);
    
    if (!todayHours || todayHours.closed) {
      return t('today.closed');
    }
    
    return `${t('today')}: ${todayHours.open} - ${todayHours.close}`;
  };

  const isOpen = (): boolean => {
    if (!storeInfo.workingHours) return false;
    
    const today = new Date().getDay();
    const todayHours = storeInfo.workingHours.find(wh => wh.day === today);
    
    if (!todayHours || todayHours.closed) return false;
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    const [openHour, openMinute] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMinute] = todayHours.close.split(':').map(Number);
    
    const currentTime = currentHour * 60 + currentMinute;
    const openTime = openHour * 60 + openMinute;
    const closeTime = closeHour * 60 + closeMinute;
    
    return currentTime >= openTime && currentTime <= closeTime;
  };

  const fallbackGradients = [
    'linear-gradient(to right, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(to right, #ff758c 0%, #ff7eb3 100%)',
    'linear-gradient(to right, #f9d423 0%, #ff4e50 100%)',
    'linear-gradient(to right, #0acffe 0%, #495aff 100%)'
  ];

  return (
    <div className="bg-white shadow-md rounded-b-lg">
      <div className="relative h-48 overflow-hidden">
        {bannerImages.length > 0 ? (
          // If we have banner images, show them with rotation
          bannerImages.map((image, index) => (
            <div 
              key={index}
              className="absolute inset-0 restaurant-banner"
              style={{
                backgroundImage: `url(${image})`,
                opacity: index === currentImageIndex ? 1 : 0,
                zIndex: index === currentImageIndex ? 1 : 0,
                transition: 'opacity 1s ease-in-out'
              }}
            />
          ))
        ) : (
          // Fallback colored gradient background if no images
          <div 
            className="absolute inset-0 restaurant-banner"
            style={{
              background: fallbackGradients[Math.floor(Math.random() * fallbackGradients.length)]
            }}
          />
        )}
        
        {/* Semi-transparent overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-30 z-10"></div>
        
        <div className="absolute bottom-0 left-0 w-full p-4 text-white z-20">
          <h1 className="text-2xl font-bold">{storeInfo.name}</h1>
          <div className="flex items-center mt-1">
            <span className={`status-indicator ${isOpen() ? 'status-open' : 'status-closed'}`}></span>
            <span className="text-sm">{isOpen() ? t('status.open') : t('status.closed')}</span>
            <span className="mx-2">•</span>
            <span className="text-sm">{storeInfo.mainCategoryId === 'desert' ? 'Tatlı' : storeInfo.mainCategoryId}</span>
          </div>
        </div>
      </div>
      
      <div className="p-4">
        <p className="text-gray-600 mb-4">{storeInfo.description}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center">
            <Clock className="w-5 h-5 text-gray-500 mr-2" />
            <span className="text-sm">{formatWorkingHours(storeInfo.workingHours)}</span>
          </div>
          
          {storeInfo.phone && (
            <div className="flex items-center">
              <Phone className="w-5 h-5 text-gray-500 mr-2" />
              <a href={`tel:${storeInfo.phone}`} className="text-sm text-blue-600">{storeInfo.phone}</a>
            </div>
          )}
          
          {storeInfo.address && (
            <div className="flex items-center col-span-1 md:col-span-2">
              <MapPin className="w-5 h-5 text-gray-500 mr-2 flex-shrink-0" />
              <span className="text-sm">{storeInfo.address}</span>
            </div>
          )}
          
          {storeInfo.instagramAddress && (
            <div className="flex items-center">
              <Instagram className="w-5 h-5 text-gray-500 mr-2" />
              <a 
                href={`https://instagram.com/${storeInfo.instagramAddress}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600"
              >
                @{storeInfo.instagramAddress}
              </a>
            </div>
          )}
        </div>
        
        {/* {storeInfo.properties && (
          <div className="mt-4 pt-4 border-t">
            <h3 className="text-sm font-medium mb-2">{t('features')}</h3>
            <div className="flex flex-wrap gap-2">
              {storeInfo.properties.callWaiter && (
                <span className="feature-tag">{t('features.callWaiter')}</span>
              )}
              {storeInfo.properties.requestCheck && (
                <span className="feature-tag">{t('features.requestCheck')}</span>
              )}
              {storeInfo.properties.reservation && (
                <span className="feature-tag">{t('features.reservation')}</span>
              )}
              {storeInfo.properties.pay && (
                <span className="feature-tag">{t('features.pay')}</span>
              )}
              {storeInfo.properties.order && (
                <span className="feature-tag">{t('features.order')}</span>
              )}
              {storeInfo.properties.delivery && (
                <span className="feature-tag">{t('features.delivery')}</span>
              )}
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default RestaurantInfo;